import { useMemo, useState } from 'react';
import {
  Alert,
  AlertDescription,
  AlertIcon,
  Badge,
  Box,
  Button,
  Divider,
  Grid,
  GridItem,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Stack,
  Text,
} from '@chakra-ui/react';
import { Scanner } from '@yudiel/react-qr-scanner';
import useToasts from './useToasts';

interface QrScannerModalProps {
  isOpen: boolean;
  onClose: () => void;
  apiBaseUrl: string;
  token: string | null;
}

interface SubscriptionScanData {
  cpf?: string;
  name?: string;
  liveTransactionStatus?: string;
  tournamentDescription?: string;
  tournamentTime?: string;
  subscriptionType?: string;
  subscriptionStatus?: string;
  subscriptionDate?: string;
  amount?: number;
  liveTransactionId?: string;
}

type ScanAction = 'validate' | 'invalidate';

const formatCurrencyBr = (value?: number) =>
  new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
    minimumFractionDigits: 2,
  }).format((value ?? 0) / 100);

const formatDateTime = (value?: string) =>
  value
    ? new Date(value).toLocaleString('pt-BR', {
        dateStyle: 'short',
        timeStyle: 'short',
      })
    : '—';

const formatCpfDisplay = (value?: string) => {
  const digits = (value || '').replace(/\D/g, '').slice(0, 11);
  if (!digits) return '—';
  const part1 = digits.slice(0, 3);
  const part2 = digits.slice(3, 6);
  const part3 = digits.slice(6, 9);
  const part4 = digits.slice(9, 11);
  return [part1, part2, part3].filter(Boolean).join('.') + (part4 ? `-${part4}` : '');
};

const getStatusLabel = (value?: string) => {
  switch ((value || '').toUpperCase()) {
    case 'SUCCESS':
    case 'DONE':
      return 'Sucesso';
    case 'PENDING':
      return 'Pendente';
    case 'FAILED':
    case 'ERROR':
      return 'Falhou';
    case 'CANCELLED':
      return 'Cancelado';
    default:
      return value || 'Não informado';
  }
};

const getStatusColor = (value?: string) => {
  switch ((value || '').toUpperCase()) {
    case 'SUCCESS':
    case 'DONE':
      return 'green';
    case 'PENDING':
      return 'yellow';
    case 'FAILED':
    case 'ERROR':
      return 'red';
    case 'CANCELLED':
      return 'gray';
    default:
      return 'blue';
  }
};

const getSubscriptionLabel = (value?: string) => {
  switch ((value || '').toUpperCase()) {
    case 'BUY_IN':
      return 'Buy-in';
    case 'REBUY':
      return 'Rebuy';
    case 'ADD_ON':
      return 'Add-on';
    default:
      return value || 'Não informado';
  }
};

const normalizeScanPayload = (payload: unknown, rawValue: string): SubscriptionScanData & { rawValue: string } => {
  const base =
    (payload as { data?: Record<string, unknown> })?.data ||
    (payload as Record<string, unknown>) ||
    {};

  const parseNumber = (value: unknown): number | undefined => {
    if (typeof value === 'number') return value;
    if (typeof value === 'string') {
      const numeric = Number(value);
      return Number.isFinite(numeric) ? numeric : undefined;
    }
    return undefined;
  };

  return {
    rawValue,
    cpf: typeof base.cpf === 'string' ? base.cpf : undefined,
    name: typeof base.name === 'string' ? base.name : undefined,
    liveTransactionStatus:
      typeof base.LiveTransactionStatus === 'string'
        ? base.LiveTransactionStatus
        : typeof base.liveTransactionStatus === 'string'
          ? base.liveTransactionStatus
          : undefined,
    tournamentDescription: typeof base.tournamentDescription === 'string' ? base.tournamentDescription : undefined,
    tournamentTime: typeof base.tournamentTime === 'string' ? base.tournamentTime : undefined,
    subscriptionType: typeof base.subscriptionType === 'string' ? base.subscriptionType : undefined,
    subscriptionStatus:
      typeof base.subscriptionStatus === 'string'
        ? base.subscriptionStatus
        : typeof base.SubscriptionStatus === 'string'
          ? base.SubscriptionStatus
          : undefined,
    subscriptionDate: typeof base.subscriptionDate === 'string' ? base.subscriptionDate : undefined,
    amount: parseNumber(base.amount),
    liveTransactionId: typeof base.liveTransactionId === 'string' ? base.liveTransactionId : undefined,
  };
};

const tryParseLocalPayload = (rawValue: string) => {
  try {
    const parsed = JSON.parse(rawValue);
    return normalizeScanPayload(parsed, rawValue);
  } catch (error) {
    console.error('Falha ao interpretar o QR localmente:', error);
    return null;
  }
};

const safeParseJson = (text: string) => {
  try {
    return text ? JSON.parse(text) : {};
  } catch (error) {
    console.error('Resposta não era JSON válido:', error);
    return null;
  }
};

const QrScannerModal = ({ isOpen, onClose, apiBaseUrl, token }: QrScannerModalProps) => {
  const { showToast } = useToasts();
  const [scanResult, setScanResult] = useState<(SubscriptionScanData & { rawValue: string }) | null>(null);
  const [scanError, setScanError] = useState('');
  const [rawValue, setRawValue] = useState('');
  const [isFetching, setIsFetching] = useState(false);
  const [isActionLoading, setIsActionLoading] = useState(false);

  const clearState = () => {
    setScanResult(null);
    setScanError('');
    setRawValue('');
    setIsFetching(false);
    setIsActionLoading(false);
  };

  const resolveBaseUrl = () =>
    apiBaseUrl.includes('newapi.chippix.com.br')
      ? apiBaseUrl
      : apiBaseUrl.startsWith('/api')
        ? 'https://newapi.chippix.com.br/v1'
        : apiBaseUrl.replace('sandbox.api.chippix.com.br', 'newapi.chippix.com.br');

  const handleClose = () => {
    clearState();
    onClose();
  };

  const fetchScanDetails = async (code: string) => {
    if (!code || isFetching) return;
    setIsFetching(true);
    setScanError('');
    setRawValue(code);
    setScanResult(null);

    try {
      const baseUrl = resolveBaseUrl();

      const response = await fetch(
        `${baseUrl}/tournament/subscription/qrcode?url=${encodeURIComponent(code)}`,
        {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
        }
      );

      const text = await response.text();
      const payload = safeParseJson(text);

      if (!response.ok) {
        const fallbackMessage = payload && typeof payload === 'object' && 'message' in payload ? (payload as { message?: string }).message : null;
        const readableText = text?.slice(0, 180) || '';
        throw new Error(
          fallbackMessage ||
            `Não foi possível consultar o QR Code (status ${response.status}). ${readableText ? `Detalhes: ${readableText}` : ''}`.trim()
        );
      }

      if (!payload) {
        throw new Error('A resposta da consulta não veio em JSON. Verifique o código lido.');
      }

      const normalized = normalizeScanPayload(payload as unknown, code);
      setScanResult(normalized);
      showToast({
        title: 'QR Code lido',
        description: 'Dados encontrados e prontos para validação.',
        status: 'success',
      });
    } catch (error) {
      const fallback = tryParseLocalPayload(code);
      if (fallback) {
        setScanResult(fallback);
      }
      const message = error instanceof Error ? error.message : 'Erro inesperado ao consultar o QR Code.';
      setScanError(message);
    } finally {
      setIsFetching(false);
    }
  };

  const handleScan = (detectedCodes: Array<{ rawValue?: string }> | undefined) => {
    if (!detectedCodes?.length || isFetching || scanResult) return;
    const value = detectedCodes[0]?.rawValue;
    if (!value) return;
    fetchScanDetails(value);
  };

  const handleAction = async (action: ScanAction) => {
    if (!scanResult?.liveTransactionId) {
      setScanError('Nenhum ID de transação foi encontrado para validar/invalidar.');
      return;
    }
    setIsActionLoading(true);
    setScanError('');

    const baseUrl = resolveBaseUrl();

    try {
      const response = await fetch(`${baseUrl}/tournament/subscription/qrcode`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({
          lt_id: scanResult.liveTransactionId,
          status: action === 'validate' ? 'VALIDATED' : 'INVALIDATED',
        }),
      });

      const text = await response.text();
      const payload = safeParseJson(text);

      if (!response.ok) {
        throw new Error((payload as { message?: string } | null)?.message || 'Não foi possível atualizar o status desta inscrição.');
      }

      if (!payload) {
        throw new Error('A resposta da ação não veio em JSON.');
      }

      showToast({
        title: action === 'validate' ? 'Inscrição validada' : 'Inscrição invalidada',
        description: (payload as { message?: string }).message || 'Ação registrada com sucesso.',
        status: 'success',
      });

      const subscriptionStatus =
        (payload as { data?: { subscriptionStatus?: string } } | null)?.data?.subscriptionStatus;

      if (action === 'validate' && subscriptionStatus === 'VALIDATED') {
        clearState();
      }
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Erro inesperado ao enviar a ação.';
      setScanError(message);
      showToast({
        title: 'Não foi possível concluir',
        description: message,
        status: 'error',
      });
    } finally {
      setIsActionLoading(false);
    }
  };

  const infoItems = useMemo(
    () => [
      {
        label: 'Torneio',
        value: scanResult?.tournamentDescription || 'Sem descrição',
      },
      { label: 'Status', value: getStatusLabel(scanResult?.liveTransactionStatus), isStatus: true },
      {
        label: 'Status da inscrição',
        value: getStatusLabel(scanResult?.subscriptionStatus),
        isStatus: true,
      },
      { label: 'Nome do jogador', value: scanResult?.name || '—' },
      { label: 'CPF', value: formatCpfDisplay(scanResult?.cpf) },
      { label: 'Tipo de inscrição', value: getSubscriptionLabel(scanResult?.subscriptionType) },
      {
        label: 'Valor pago',
        value: formatCurrencyBr(scanResult?.amount),
      },
      { label: 'Data da inscrição', value: formatDateTime(scanResult?.subscriptionDate) },
      { label: 'Horário do torneio', value: formatDateTime(scanResult?.tournamentTime) },
      {
        label: 'ID da transação',
        value: scanResult?.liveTransactionId || '—',
      },
    ],
    [scanResult]
  );

  return (
    <Modal isOpen={isOpen} onClose={handleClose} size="4xl">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>Escanear QR Code</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <Stack spacing={5}>
            <Text color="text.700">
              Aponte a câmera para o QR Code do jogador. Assim que o código for lido, buscaremos os dados da inscrição
              e você poderá validar ou invalidar a entrada.
            </Text>

            {scanError && (
              <Alert status="error" borderRadius="lg">
                <AlertIcon />
                <AlertDescription>{scanError}</AlertDescription>
              </Alert>
            )}

            {!scanResult && (
              <Box
                borderWidth="1px"
                borderColor="gray.200"
                _dark={{ borderColor: 'gray.700' }}
                rounded="lg"
                overflow="hidden"
                bg="black"
              >
                {isOpen && (
                  <Scanner
                    onScan={handleScan}
                    onError={(error) => setScanError(error instanceof Error ? error.message : 'Erro ao acessar a câmera.')}
                    scanDelay={800}
                    paused={isFetching}
                    components={{
                      finder: true,
                    }}
                    constraints={{
                      facingMode: 'environment',
                    }}
                    styles={{
                      container: {
                        height: '320px',
                      },
                    }}
                  />
                )}
              </Box>
            )}

            {scanResult ? (
              <Stack spacing={4}>
                <Divider />
                <Text fontWeight="semibold" color="text.900">
                  Dados da inscrição
                </Text>
                <Grid templateColumns={{ base: '1fr', md: 'repeat(2, 1fr)' }} gap={4}>
                  {infoItems.map((item) => (
                    <GridItem key={item.label} borderWidth="1px" borderColor="gray.200" rounded="md" p={3} bg="white">
                      <Text fontSize="sm" color="text.600">
                        {item.label}
                      </Text>
                      {item.isStatus ? (
                        <Badge mt={1} colorScheme={getStatusColor(scanResult.liveTransactionStatus)}>
                          {item.value}
                        </Badge>
                      ) : (
                        <Text fontWeight="semibold" color="text.900">
                          {item.value}
                        </Text>
                      )}
                    </GridItem>
                  ))}
                </Grid>
              </Stack>
            ) : (
              <Text color="text.600">Aguardando leitura do QR Code para exibir os dados.</Text>
            )}
          </Stack>
        </ModalBody>
        <ModalFooter>
          <Stack direction={{ base: 'column', md: 'row' }} spacing={3} w="100%" align={{ base: 'stretch', md: 'center' }} justify="space-between">
            <Text fontSize="sm" color="text.600">
              Código lido: {rawValue || '—'}
            </Text>
            <Stack direction={{ base: 'column', sm: 'row' }} spacing={3} align={{ base: 'stretch', sm: 'center' }}>
              <Button variant="ghost" onClick={clearState} isDisabled={isFetching} w={{ base: '100%', sm: 'auto' }}>
                Escanear novamente
              </Button>
              <Button colorScheme="green" onClick={() => handleAction('validate')} isLoading={isActionLoading} isDisabled={!scanResult} w={{ base: '100%', sm: 'auto' }}>
                Validar inscrição
              </Button>
              <Button colorScheme="red" variant="outline" onClick={() => handleAction('invalidate')} isLoading={isActionLoading} isDisabled={!scanResult} w={{ base: '100%', sm: 'auto' }}>
                Invalidar
              </Button>
              <Button variant="ghost" onClick={handleClose} w={{ base: '100%', sm: 'auto' }}>
                Fechar
              </Button>
            </Stack>
          </Stack>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default QrScannerModal;
